import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { getEmployeeCourses, addEmployeeCourse, addEmployeeCourseGroup, deleteEmployeeCourse, updateEmployeeCourse } from "requests/employeeCourse";
import { getGroups } from "requests/courseGroup";
import { searchCourses } from "requests/course";
import MCard from "components/Shared/MCard.tsx";
import { FILTER_TYPE_NONE } from "components/Shared/MGrid.tsx";
import { MSpinner, MGrid, MDialog, ActionButtons } from "components/Shared";
import { DATA_TYPE_DATE, DATA_TYPE_NUMBER, DATA_TYPE_BOOLEAN, DATA_TYPE_BUTTON, DATA_TYPE_DROPDOWN2 } from "typeConstants/dataTypes";
import { dateToStringYy } from "components/Shared/Formatters";
import { userHasRight } from "selectors/rights";
import { EMPLOYEE_COURSE_VIEW, EMPLOYEE_COURSE_ADD, EMPLOYEE_COURSE_DELETE, EMPLOYEE_COURSE_EDIT, COURSE_VIEW } from "typeConstants/rights";
import responseHandler from "responseHandler";

export const getEditEmployeeCourseCardFields = employeeCourse => [
  { key: "dueDate", label: "Date Due", value: dateToStringYy(employeeCourse?.dueDate), dataType: DATA_TYPE_DATE },
  { key: "completedDate", label: "Date Completed", value: dateToStringYy(employeeCourse?.completedDate), dataType: DATA_TYPE_DATE },
  { key: "durationMinutes", label: "Duration in Minutes", value: employeeCourse?.durationMinutes, dataType: DATA_TYPE_NUMBER },
  { key: "notificationEnabled", label: "Notification Enabled", value: employeeCourse?.notificationEnabled, dataType: DATA_TYPE_BOOLEAN },
  { key: "score", label: "Score", value: employeeCourse?.score, dataType: DATA_TYPE_NUMBER },
];

const getNewEmployeeCourseCardFields = (isAssigningGroup, dropdownValues) => [
  isAssigningGroup ?
  { key: "courseGroupId", label: "Course Group", dataType: DATA_TYPE_DROPDOWN2, dataTypeOptions: { options: dropdownValues } }  :
  { key: "courseId", label: "Course", dataType: DATA_TYPE_DROPDOWN2, dataTypeOptions: { options: dropdownValues } },
  { key: "dueDate", label: "Date Due", dataType: DATA_TYPE_DATE },
  { key: "notificationEnabled", label: "Notification Enabled", dataType: DATA_TYPE_BOOLEAN },
];

const EmployeeCourses = ({
    employeeId,
    className,
    makeSuccessToast,
    canViewEmployeeCourses,
    canAddEmployeeCourses,
    canDeleteEmployeeCourses,
    canEditEmployeeCourses,
    canViewCourses,
  }) => {
  const [employeeCourses, setEmployeeCourses] = useState(null);
  const [selectedEmployeeCourse, setSelectedEmployeeCourse] = useState(null);
  const [selectedGridIndex, setSelectedGridIndex] = useState(null);
  const [isEditingCourse, setIsEditingCourse] = useState(null);
  const [isAddingCourse, setIsAddingCourse] = useState(false);
  const [isAddingCourseGroup, setIsAddingCourseGroup] = useState(false);
  const [courses, setCourses] = useState(null);
  const [courseGroups, setCourseGroups] = useState();
  const [showRemoveDialog, setShowRemoveDialog] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const mounted = useRef(true);
  const instance = useRef({ controlMethods: null, lastGroupId: null, lastCourseId: null });

  const getEmployeeCoursesWrapped = () => {
    setEmployeeCourses(null);
    setSelectedEmployeeCourse(null);
    setSelectedGridIndex(null);
    getEmployeeCourses(employeeId, setEmployeeCourses, mounted);
  };

  useEffect(() => {
    //reset entire component state on reload
    setEmployeeCourses(null);
    setSelectedEmployeeCourse(null);
    setIsEditingCourse(null);
    setIsAddingCourse(false);
    setIsAddingCourseGroup(false);
    getEmployeeCoursesWrapped();
  }, [employeeId]);

  useEffect(() => () => mounted.current = false, []);

  if (!employeeCourses || !canViewEmployeeCourses) return <MSpinner />;

  const copyButton = (row) => row.fileName && <i className="md-icon material-icons">content_copy</i>;

  const colDefs = [
    { name: "Course Name", dataSource: "course.name" },
    { name: "Category", dataSource: "course.courseGroup.name" },
    { name: "Created", dataSource: "createDate", width: 68, dataType: DATA_TYPE_DATE },
    { name: "Completed", dataSource: "completedDate", width: 87, dataType: DATA_TYPE_DATE },
    { name: "Due Date", dataSource: "dueDate", width: 77, dataType: DATA_TYPE_DATE },
    { name: "Expected Minutes", dataSource: "course.expectedMinutes", width: 140, dataType: DATA_TYPE_NUMBER },
    { name: "Duration (Minutes)", dataSource: "durationMinutes", width: 140, dataType: DATA_TYPE_NUMBER },
    { name: "Notification Enabled", dataSource: "notificationEnabled", width: 147, dataType: DATA_TYPE_BOOLEAN },
    { name: "Score", dataSource: "score", width: 57, dataType: DATA_TYPE_NUMBER },
    { name: "Active", dataSource: "isActive", width: 60, dataType: DATA_TYPE_BOOLEAN },
    { name: "File", dataSource: copyButton, exportValue: "fileName", width: 57, dataType: DATA_TYPE_BUTTON, tooltip: () => "Click to copy file location to clipboard", filterType: FILTER_TYPE_NONE },
  ].map(c => ({ key: c.name, ...c }));

  const resetGridState = () => {
    setSelectedGridIndex(null);
    setSelectedEmployeeCourse(null);
  };

  const onAddCourseClicked = () => {
    if (!courses) searchCourses("", false, courses => setCourses(courses), mounted);
    setIsAddingCourse(true);
    setIsAddingCourseGroup(false);
    resetGridState();
  };

  const onAddCourseGroupClicked = () => {
    if (!courseGroups) getGroups(groups => setCourseGroups(groups), mounted);
    setIsAddingCourseGroup(true);
    setIsAddingCourse(false);
    resetGridState();
    instance.current.lastGroupId = null;
  };

  const onRemoveCourseClicked = force => {
    if (force) {
      if (selectedEmployeeCourse) {
        setEmployeeCourses(null);
        setShowRemoveDialog(false);
        resetGridState();
        deleteEmployeeCourse(selectedEmployeeCourse.id, getEmployeeCoursesWrapped, mounted);
      }
    } else {
      setShowRemoveDialog(true);
    }
  };

  const onEmployeeCourseClicked = i => {
    setSelectedEmployeeCourse(employeeCourses[i]);
    setSelectedGridIndex(i);
  };

  const copyToClipboard = (index) => {
    navigator.clipboard.writeText(employeeCourses[index].fileName);
    makeSuccessToast("Path copied to clipboard");
  };

  const EmployeeCourseGrid = () =>
    <MGrid
      columnDefinitions={colDefs}
      data={employeeCourses}
      disableFilters
      onRowClick={i => onEmployeeCourseClicked(i)}
      onCellButtonClick={copyToClipboard}
      rowLastClicked={selectedGridIndex}
    />;

  const onEditCancelled = () => {
    setIsSaving(false);
    setIsEditingCourse(false);
    setIsAddingCourse(false);
    setIsAddingCourseGroup(false);
  };

  const onEditPressed = () => {
    setIsEditingCourse(true);
  };

  const onCardEditChanged = (_, employeeCourse) => {
    if (isAddingCourseGroup) {
      if (employeeCourse.courseGroupId !== instance.current.lastGroupId) {
        let group = courseGroups.find(g => g.id === employeeCourse.courseGroupId);
        let date = new Date();
        date.setDate(date.getDate() + group.daysTillDue);
        instance.current.controlMethods.setEditData("dueDate", dateToStringYy(date));
        instance.current.controlMethods.setEditData("notificationEnabled", group.notificationEnabled);
      }
      instance.current.lastGroupId = employeeCourse.courseGroupId;
    } else if (isAddingCourse) {
      //someone that code reviews this please make sure i removed this, not sure if i need it yet
    //   if (employeeCourse.courseId !== instance.current.lastCourseId) {
    //     let course = courses.find(c => c.id === employeeCourse.courseId);
    //     instance.current.controlMethods.setEditData("notificationEnabled", course.notificationEnabled);
    //     instance.current.lastCourseId = employeeCourse.courseId;
    //   }
    }
  };

  const onEditFinished = (_, employeeCourse) => {
    if (isSaving) return false;

    setIsSaving(true);

    const onSuccess = () => {
      setIsSaving(false);
      setIsEditingCourse(false);
      setIsAddingCourse(false);
      setIsAddingCourseGroup(false);
      getEmployeeCoursesWrapped();
    };

    if (isAddingCourseGroup) {
      employeeCourse.employeeId = employeeId;
      addEmployeeCourseGroup(employeeCourse, onSuccess, () => setIsSaving(false), mounted);
    } else if (isAddingCourse) {
      employeeCourse.employeeId = employeeId;
      addEmployeeCourse(employeeCourse, onSuccess, () => setIsSaving(false), mounted);
    } else if (isEditingCourse) {
      employeeCourse.id = selectedEmployeeCourse.id;
      updateEmployeeCourse({ ...instance.current.controlMethods.getEditedDataObject(true), ...employeeCourse }, onSuccess, () => setIsSaving(false), mounted);
    }

    return false;
  };

  let isAddingOrEditing = isEditingCourse || isAddingCourse || isAddingCourseGroup;

  let cardActions = [
    (!isAddingOrEditing && selectedEmployeeCourse && canDeleteEmployeeCourses) && { icon: "delete", helpText: "Remove Course", onClick: () => onRemoveCourseClicked(false) },
    (!isAddingOrEditing && selectedEmployeeCourse && canEditEmployeeCourses) && { icon: "edit", helpText: "Edit Course", onClick: onEditPressed },
    (!isAddingOrEditing && canAddEmployeeCourses && canViewCourses) && { icon: "add", helpText: "Assign Course", onClick: onAddCourseClicked },
    (!isAddingOrEditing && canAddEmployeeCourses && canViewCourses) && { icon: "playlist_add", helpText: "Assign Entire Course Category", onClick: onAddCourseGroupClicked },
  ].filter(c => c);

  let cardFields;
  let cardTitle = "Employee Courses";

  if (isEditingCourse) {
    cardFields = getEditEmployeeCourseCardFields(selectedEmployeeCourse);
    cardTitle = `Editing Course ${selectedEmployeeCourse?.course.name}`;
  } else if (isAddingCourse) {
    if (courses) {
      let mappedCourses = courses.filter(course => !employeeCourses.some(c => c.course.id === course.id && c.isActive)).map(c => ({ label: c.name, value: c.id }));
      cardFields = getNewEmployeeCourseCardFields(false, mappedCourses);
    }
    cardTitle = "Assigning Course";
  } else if (isAddingCourseGroup) {
    cardFields = courseGroups && getNewEmployeeCourseCardFields(true, courseGroups.map(g => ({ label: g.name, value: g.id })));
    cardTitle = "Assigning Course Group";
  }

  return (<>
    <MCard
      key={isAddingOrEditing ? "editing" : "viewing"} //change the key so the card remounts because we change some props that the card doesnt support changing on the fly
      title={cardTitle}
      className={"employee-courses " + (className ?? "")}
      actions={cardActions}
      fields={isAddingOrEditing && cardFields}
      startInEditMode={isAddingOrEditing}
      isEditable={isAddingOrEditing}
      onEditFinished={onEditFinished}
      onEditChanged={onCardEditChanged}
      onEditCancelled={onEditCancelled}
      isFetching={isAddingOrEditing && !cardFields}
      getControlMethods={cm => instance.current.controlMethods = cm}
    >
      {!isEditingCourse && !isAddingCourse && !isAddingCourseGroup && EmployeeCourseGrid()}
    </MCard>
    <MDialog
      visible={showRemoveDialog}
      width={350}
    >
      Are you sure you want to remove this course?
      <ActionButtons buttonPropSets={[
        { children: "Yes", onClick: () => onRemoveCourseClicked(true) },
        { children: "No", onClick: () => setShowRemoveDialog(false) },
      ]} />
    </MDialog>
  </>);
};

EmployeeCourses.propTypes = {
  employeeId: PropTypes.number.isRequired,
  className: PropTypes.string,
  makeSuccessToast: PropTypes.func.isRequired,
  canViewEmployeeCourses: PropTypes.bool.isRequired,
  canAddEmployeeCourses: PropTypes.bool.isRequired,
  canDeleteEmployeeCourses: PropTypes.bool.isRequired,
  canEditEmployeeCourses: PropTypes.bool.isRequired,
  canViewCourses: PropTypes.bool.isRequired,
};

const mapStateToProps = state => {
  return {
    canViewEmployeeCourses: userHasRight(EMPLOYEE_COURSE_VIEW)(state),
    canAddEmployeeCourses: userHasRight(EMPLOYEE_COURSE_ADD)(state),
    canDeleteEmployeeCourses: userHasRight(EMPLOYEE_COURSE_DELETE)(state),
    canEditEmployeeCourses: userHasRight(EMPLOYEE_COURSE_EDIT)(state),
    canViewCourses: userHasRight(COURSE_VIEW)(state),
  };
};

const mapDispatchToProps = (dispatch) => ({
  makeSuccessToast(text) {
    responseHandler.customToast(text, "success", dispatch);
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(EmployeeCourses);
